// Mar 2026 v39 — "I miei contratti" (Customer)
// Espone i contratti attualmente applicabili con le regole PFA leggibili
// (GLOBAL / categoria / articolo) + download PDF quando disponibile.
// NON mostra UUID interni, scope_type raw, o dati amministrativi riservati.
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../auth/AuthProvider";
import { Section, Card, Alert, Badge, Button } from "../design-system";
import { PfaTiersTable } from "../components/PfaTiers";
import { PfaScopeSummary } from "../components/PfaScopeSummary";

const it = (d) => {
  if (!d) return "—";
  const [y, m, day] = d.split("-");
  return `${day}/${m}/${y}`;
};

const nkg = (v) => (v == null ? "—" : Number(v).toLocaleString("it-IT", {
  minimumFractionDigits: 0, maximumFractionDigits: 3,
}));

function ruleLabel(r) {
  // v41 Step 3 — display_name from backend (fallback GLOBAL→"PFA Generale",
  // CATEGORY→"PFA per categoria", PRODUCT→"PFA per articoli"). Never mixed.
  if (r.display_name) return r.display_name;
  if (r.name) return r.name;
  if (r.scope === "PRODUCT") return "PFA per articoli";
  if (r.scope === "CATEGORY") return "PFA per categoria";
  return "PFA Generale";
}

export default function CustomerContractsPage() {
  const { apiGet, token } = useAuth();
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState(null);
  const backend = process.env.REACT_APP_BACKEND_URL || "";

  useEffect(() => {
    (async () => {
      try {
        const d = await apiGet("/api/customer/contracts");
        setRows(Array.isArray(d) ? d : []);
      } catch (e) {
        setErr(e?.message || String(e));
        setRows([]);
      }
    })();
  }, [apiGet]);

  const downloadPdf = async (contract) => {
    if (!contract.has_document) return;
    try {
      const res = await fetch(`${backend}${contract.document_url}`, {
        headers: { Authorization: `Bearer ${token || ""}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch (e) {
      setErr(`Errore download PDF: ${e?.message || e}`);
    }
  };

  return (
    <Section
      title="I miei contratti"
      subtitle="Contratti commerciali attualmente attivi con la tua azienda."
      data-testid="customer-contracts-page"
    >
      {err && <Alert tone="danger" data-testid="customer-contracts-error">{String(err)}</Alert>}
      {!rows && <Card><Card.Body className="muted">Caricamento…</Card.Body></Card>}

      {rows && rows.length === 0 && (
        <Alert tone="info" data-testid="customer-contracts-empty">
          Nessun contratto attivo al momento. Contatta il tuo referente commerciale
          per informazioni.
        </Alert>
      )}

      {rows && rows.map((c) => (
        <Card key={c.contract_id} data-testid={`customer-contract-card-${c.contract_id}`}
              style={{ marginBottom: 14 }}>
          <Card.Header>
            <div style={{ display: "flex", justifyContent: "space-between",
                          alignItems: "center", flexWrap: "wrap", gap: 8 }}>
              <div>
                <strong style={{ fontSize: "1.05rem" }}
                        data-testid={`customer-contract-name-${c.contract_id}`}>{c.name}</strong>
                <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>
                  <code>{c.code}</code>{" · "}
                  {it(c.valid_from)} – {it(c.valid_to)}
                </div>
              </div>
              <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                <Badge tone={c.is_active ? "success" : "neutral"}
                       data-testid={`customer-contract-status-${c.contract_id}`}>
                  {c.is_active ? "Attivo" : "Chiuso"}
                </Badge>
                {c.has_document && (
                  <Button size="sm" onClick={() => downloadPdf(c)}
                          data-testid={`customer-contract-pdf-${c.contract_id}`}>
                    Scarica contratto (PDF)
                  </Button>
                )}
              </div>
            </div>
          </Card.Header>
          <Card.Body>
            <div style={{ fontSize: 13, color: "#334155", marginBottom: 8 }}>
              <strong>Premio Fine Anno</strong>
            </div>
            {c.pfa_rules.length === 0 ? (
              <p className="muted" style={{ margin: 0, fontSize: 13 }}>
                Nessuna regola PFA configurata su questo contratto.
              </p>
            ) : (
              <ul style={{ margin: 0, padding: 0, listStyle: "none" }}>
                {c.pfa_rules.map((r) => {
                  const pct = Math.min(100, Math.max(0, Number(r.percentage) || 0));
                  return (
                    <li key={r.pfa_id}
                        data-testid={`customer-pfa-rule-${r.pfa_id}`}
                        style={{ padding: "10px 0", borderTop: "1px solid #f1f5f9" }}>
                      <div style={{ display: "flex", justifyContent: "space-between",
                                    gap: 8, flexWrap: "wrap" }}>
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <div style={{ fontSize: 14, fontWeight: 600, wordBreak: "break-word" }}
                               data-testid={`customer-pfa-rule-label-${r.pfa_id}`}>
                            {ruleLabel(r)}
                          </div>
                          <PfaScopeSummary pfa={r} testidPrefix={`customer-pfa-rule-${r.pfa_id}`} />
                        </div>
                        <div style={{ fontSize: 13, color: "#64748b" }}>
                          {it(r.period_start)} – {it(r.period_end)}
                        </div>
                      </div>
                      <div style={{ display: "grid",
                                    gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
                                    gap: 8, marginTop: 8 }}>
                        <MetricBox label="Target"     value={`${nkg(r.target_kg)} kg`} />
                        <MetricBox label="Maturato"   value={`${nkg(r.matured_kg)} kg`}
                                   testid={`customer-pfa-matured-${r.pfa_id}`} />
                        <MetricBox label="Residuo"    value={`${nkg(r.residual_kg)} kg`} />
                        {r.cumulative_reward_percent > 0 && (
                          <MetricBox label="Premio maturato"
                                     value={`${Number(r.cumulative_reward_percent).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 3 })}%`}
                                     tone="success" />
                        )}
                      </div>
                      <div style={{ background: "#e5e7eb", borderRadius: 4,
                                    marginTop: 8, height: 6, overflow: "hidden" }}>
                        <div style={{ width: `${pct}%`, height: "100%",
                                      background: pct >= 100 ? "#10b981"
                                                : pct >= 75  ? "#0ea5e9"
                                                : "#0F4C81" }} />
                      </div>
                      {r.tiers && r.tiers.length > 0 && (
                        <div style={{ marginTop: 12 }}>
                          <PfaTiersTable
                            tiers={r.tiers}
                            maturedKg={Number(r.matured_kg) || 0}
                            testidPrefix={`contract-pfa-tier-${r.pfa_id}`}
                          />
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </Card.Body>
        </Card>
      ))}

      {rows && rows.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <Link to="/customer/pfa" data-testid="customer-contracts-go-pfa">
            <Button size="sm" variant="ghost">Vedi dettaglio PFA →</Button>
          </Link>
        </div>
      )}
    </Section>
  );
}

function MetricBox({ label, value, testid, tone }) {
  const bg     = tone === "success" ? "#DCFCE7" : "#F8FAFC";
  const border = tone === "success" ? "#86EFAC" : "#E2E8F0";
  return (
    <div data-testid={testid}
         style={{ padding: "6px 10px", background: bg, border: `1px solid ${border}`,
                  borderRadius: 6 }}>
      <div style={{ fontSize: 11, color: "#64748B" }}>{label}</div>
      <div style={{ fontSize: 15, fontWeight: 600, marginTop: 2 }}>{value}</div>
    </div>
  );
}
